import { useParams, Link } from 'react-router-dom';
import { comics, categories } from '@/mocks/comics';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function ComicDetailPage() {
  const { id } = useParams<{ id: string }>();
  const comic = comics.find((c) => c.id === Number(id));

  if (!comic) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-foreground-500 text-lg">漫画未找到</p>
          <Link to="/" className="text-primary-600 hover:text-primary-700 mt-4 inline-block cursor-pointer">
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const categoryName = categories.find((c) => c.slug === comic.category)?.name || comic.category;

  const totalChapters = comic.chapters;
  const chapterList = Array.from({ length: Math.min(totalChapters, 20) }, (_, i) => ({
    number: totalChapters - i,
    title: `第${totalChapters - i}话`,
    date: new Date(2025, 0, 1 + (totalChapters - i) * 7).toISOString().split('T')[0],
  }));

  const relatedComics = comics.filter((c) => c.category === comic.category && c.id !== comic.id).slice(0, 5);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${comic.title} - 51漫画网`,
    description: comic.description,
    author: {
      '@type': 'Person',
      name: comic.author,
    },
    datePublished: '2025-01-01',
    dateModified: comic.updateTime,
    publisher: {
      '@type': 'Organization',
      name: '51漫画网',
    },
  };

  return (
    <>
      <SEOHead
        title={`${comic.title} - ${comic.author} - 韩漫免费在线阅读 - 51漫画网`}
        description={`${comic.title}，${comic.author}作品。${comic.description.slice(0, 120)}，下拉式漫画全集呈现完整剧情，51漫画网免费在线阅读。`}
        keywords={`${comic.title},${comic.author},${categoryName}韩漫,韩国漫画,免费漫画,51漫画网`}
        canonicalUrl={`/comic/${comic.id}`}
        ogType="article"
        ogImage={comic.cover}
        schema={articleSchema}
      />
      <main className="pt-16">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: categoryName, href: `/category/${comic.category}` },
            { label: comic.title },
          ]}
        />

        <article className="w-full px-4 md:px-6 py-8">
          <div className="max-w-5xl mx-auto">
            <div className="flex flex-col md:flex-row gap-8">
              <div className="flex-shrink-0">
                <div className="w-48 md:w-56 aspect-[5/7] rounded-lg overflow-hidden bg-background-200">
                  <img
                    src={comic.cover}
                    alt={comic.title}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                  {comic.title}
                </h1>
                <div className="flex items-center gap-3 mt-3 flex-wrap">
                  <span className="text-sm text-foreground-600">作者：{comic.author}</span>
                  <span className="text-sm text-foreground-400">|</span>
                  <Link
                    to={`/category/${comic.category}`}
                    onClick={() => window.scrollTo(0, 0)}
                    className="text-sm text-primary-600 hover:text-primary-700 cursor-pointer"
                  >
                    {categoryName}
                  </Link>
                  <span className="text-sm text-foreground-400">|</span>
                  <span className={`text-sm px-2 py-0.5 rounded-md ${
                    comic.status === '已完结'
                      ? 'bg-accent-100 text-accent-700'
                      : 'bg-secondary-100 text-secondary-700'
                  }`}>
                    {comic.status}
                  </span>
                </div>

                <div className="flex items-center gap-4 mt-4">
                  <div className="flex items-center gap-1">
                    <span className="w-5 h-5 flex items-center justify-center text-primary-500">
                      <i className="ri-star-fill text-sm"></i>
                    </span>
                    <span className="text-lg font-bold text-primary-600">{comic.rating}</span>
                    <span className="text-sm text-foreground-500">分</span>
                  </div>
                  <span className="text-sm text-foreground-500">
                    <span className="w-5 h-5 inline-flex items-center justify-center text-foreground-400">
                      <i className="ri-eye-line text-sm"></i>
                    </span>
                    {comic.views}次阅读
                  </span>
                </div>

                <div className="mt-5 p-4 rounded-lg bg-background-100 border border-background-200/70">
                  <h2 className="text-sm font-semibold text-foreground-800 mb-2">作品简介</h2>
                  <p className="text-sm text-foreground-600 leading-relaxed">{comic.description}</p>
                </div>

                <div className="flex items-center gap-3 mt-5">
                  <button className="px-6 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap">
                    开始阅读
                    <i className="ri-play-line ml-2"></i>
                  </button>
                  <button className="px-6 py-3 bg-secondary-100 hover:bg-secondary-200 text-secondary-700 text-sm font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap">
                    <i className="ri-bookmark-line mr-2"></i>
                    收藏
                  </button>
                </div>
              </div>
            </div>

            <div className="mt-12">
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-lg font-heading font-bold text-foreground-900">章节列表</h2>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-foreground-500">共 {totalChapters} 话</span>
                  <button className="text-sm text-primary-600 hover:text-primary-700 cursor-pointer whitespace-nowrap">
                    正序 <i className="ri-arrow-up-down-line ml-1"></i>
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                {chapterList.map((ch) => (
                  <div
                    key={ch.number}
                    className="flex items-center justify-between px-4 py-3 rounded-lg bg-background-50 border border-background-200/70 hover:border-primary-200 hover:bg-primary-50/30 transition-all cursor-pointer"
                  >
                    <span className="text-sm text-foreground-800">{ch.title}</span>
                    <span className="text-xs text-foreground-400">{ch.date}</span>
                  </div>
                ))}
              </div>
              {totalChapters > 20 && (
                <div className="text-center mt-5">
                  <button className="px-4 py-2 text-sm text-primary-600 hover:text-primary-700 border border-primary-200 rounded-lg hover:bg-primary-50 transition-colors cursor-pointer whitespace-nowrap">
                    查看全部章节 ({totalChapters}话)
                  </button>
                </div>
              )}
            </div>

            {relatedComics.length > 0 && (
              <div className="mt-14">
                <h2 className="text-lg font-heading font-bold text-foreground-900 mb-5">
                  相关推荐
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
                  {relatedComics.map((rc) => (
                    <Link
                      key={rc.id}
                      to={`/comic/${rc.id}`}
                      onClick={() => window.scrollTo(0, 0)}
                      className="group cursor-pointer"
                      data-product-shop
                    >
                      <div className="relative aspect-[5/7] rounded-lg overflow-hidden mb-2 bg-background-200">
                        <img
                          src={rc.cover}
                          alt={rc.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                        />
                        <div className="absolute top-2 left-2 px-2 py-0.5 bg-primary-500 text-background-50 text-xs rounded-md">
                          {rc.rating}分
                        </div>
                      </div>
                      <h3 className="text-sm font-medium text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-1">
                        {rc.title}
                      </h3>
                      <p className="text-xs text-foreground-500 mt-0.5">{rc.author}</p>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </article>
      </main>
    </>
  );
}